import React, { useState, useEffect } from "react";
import {
    FormControl, InputLabel, Select, MenuItem,
    Table, TableHead, TableRow, TableCell, TableBody,
    Button, TextField,
} from "@mui/material";

import useAuthToken from '../functions/useAuthToken';

import type {
    OrganisationTypeDto,
    OrganisationListItemDto,
    OrganisationUpsertDto
} from "../models/Organisation";

import { useOrganisationsApi } from "../functions/OrganisationsApi";
import OrganisationDialog from "./OrganisationDialog";
import OrganisationUsersPanel from "./OrganisationUsersPanel";

import "./OrganisationsAdminStyleSheet.css";

const AdminOrganisationsPage = () => {

    const getAccessToken = useAuthToken();
    const { getTypes, list } = useOrganisationsApi();

    const [items, setItems] = useState<OrganisationListItemDto[]>([]);
    const [types, setTypes] = useState<OrganisationTypeDto[]>([]);
    const [search, setSearch] = useState("");
    const [organisationTypeId, setOrganisationTypeId] = useState<number | "">("");
    const [editing, setEditing] = useState<OrganisationListItemDto | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState<OrganisationListItemDto | null>(null);

    const load = async () => {
        const accessToken = await getAccessToken();
        if (!accessToken) return;
        const [typesRes, orgsRes] = await Promise.all([
            getTypes(accessToken),
            list(accessToken, { search, organisationTypeId })
        ]);
        setTypes(typesRes);
        setItems(orgsRes);
    };

    useEffect(() => { load(); }, []);

    const handleSearch = async () => {
        const accessToken = await getAccessToken();
        if (!accessToken) return;
        const res = await list(accessToken, { search, organisationTypeId });
        setItems(res);
    };

    const handleAdd = () => {
        setEditing(null);
        setDialogOpen(true);
    };

    const handleEdit = (item: OrganisationListItemDto) => {
        setEditing(item);
        setDialogOpen(true);
    };

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <div>
                    <h1>Organisations</h1>
                    <p>Manage auction houses, galleries, offices and companies.</p>
                </div>
                <Button variant="contained" onClick={handleAdd}>
                    Add Organisation
                </Button>
            </div>

            <div className="admin-toolbar">
                <TextField
                    size="small"
                    label="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <FormControl size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                        label="Type"
                        value={organisationTypeId}
                        onChange={(e) => setOrganisationTypeId(e.target.value as number | "")}
                    >
                        <MenuItem value="">All</MenuItem>
                        {types.map((t) => (
                            <MenuItem key={t.id} value={t.id}>{t.type}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button variant="outlined" onClick={handleSearch}>Apply</Button>
            </div>

            <div className="admin-split">
                <div className="admin-split-list">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Users</TableCell>
                                <TableCell>System</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow
                                    key={item.id}
                                    hover
                                    selected={selectedOrg?.id === item.id}
                                    onClick={() => setSelectedOrg(item)}
                                    sx={{ cursor: "pointer" }}
                                >
                                    <TableCell>{item.name}</TableCell>
                                    <TableCell>{item.organisationType}</TableCell>
                                    <TableCell>{item.description}</TableCell>
                                    <TableCell>{item.activeUserCount}</TableCell>
                                    <TableCell>{item.isSystemOrg ? "Yes" : "No"}</TableCell>
                                    <TableCell align="right">
                                        <span className="row-actions">
                                            <Button size="small" onClick={(e) => { e.stopPropagation(); handleEdit(item); }}>
                                                Edit
                                            </Button>
                                        </span>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>

                {selectedOrg ? (
                    <div className="admin-split-detail">
                        <div className="detail-panel-header">
                            <h2>{selectedOrg.name}</h2>
                            <p>{selectedOrg.organisationType}</p>
                        </div>
                        <OrganisationUsersPanel organisationId={selectedOrg.id} />
                    </div>
                ) : (
                    <div className="admin-split-detail admin-split-detail--empty">
                        <p>Select an organisation to manage its users.</p>
                    </div>
                )}
            </div>

            <OrganisationDialog
                open={dialogOpen}
                organisation={editing}
                types={types}
                onClose={() => setDialogOpen(false)}
                onSaved={async () => {
                    setDialogOpen(false);
                    await load();
                }}
            />
        </div>
    );
};

export default AdminOrganisationsPage;